import { pipe, shuffle, take } from "rambda"

export const BATCH_SIZE = 20
const STORAGE_KEY = "poems.numberOfRepeats"
const APP_SETTINGS_KEY = "app-settings"

export interface PoemBatchState {
  currentSet: string[]
  previousIds: string[]
  setIsPlayedTimes: number
}

/**
 * Read numberOfRepeats from the app-settings localStorage entry.
 * Default: 2.
 */
export function getRepeatsConfig(): number {
  try {
    const raw = localStorage.getItem(APP_SETTINGS_KEY)
    if (!raw) return 2
    const parsed = JSON.parse(raw)
    const value = parsed.haveYouSeenIt?.poemsNumberOfRepeats ?? 2
    console.log("[poem-batch] getRepeatsConfig →", value)
    return value
  } catch {
    console.warn("[poem-batch] getRepeatsConfig fallback to 2 (parse error)")
    return 2
  }
}

/** Retrieve current state, or initial if missing. */
export function getState(): PoemBatchState {
  const state = readState() ?? initialBatchState()
  console.log(
    "[poem-batch] getState →",
    `currentSet.length=${state.currentSet.length}`,
    `previousIds.length=${state.previousIds.length}`,
    `setIsPlayedTimes=${state.setIsPlayedTimes}`,
  )
  return state
}

/**
 * Mark the current batch as completed.
 *
 * - Increments `setIsPlayedTimes`.
 * - If `setIsPlayedTimes >= numberOfRepeats`, the batch is rotated:
 *   current → previous, and a fresh batch is drawn from remaining IDs.
 * - If fewer than `BATCH_SIZE` poems remain, the cycle resets
 *   (previousIds cleared, starts from the full set).
 * - Persists before returning.
 */
export function markSetCompleted(
  allIds: string[],
  state: PoemBatchState,
  numberOfRepeats: number,
): PoemBatchState {
  console.log(
    "[poem-batch] markSetCompleted → BEFORE:",
    `setIsPlayedTimes=${state.setIsPlayedTimes}`,
    `currentSet.length=${state.currentSet.length}`,
    `previousIds.length=${state.previousIds.length}`,
  )

  let nextState: PoemBatchState = {
    ...state,
    setIsPlayedTimes: state.setIsPlayedTimes + 1,
  }

  if (nextState.setIsPlayedTimes >= numberOfRepeats) {
    const dedupedPrevious = [
      ...new Set([...state.previousIds, ...state.currentSet]),
    ]
    const remaining = allIds.filter(id=> !dedupedPrevious.includes(id))

    console.log(
      "[poem-batch] markSetCompleted → ROTATING",
      `(${nextState.setIsPlayedTimes} >= ${numberOfRepeats})`,
      `remaining=${remaining.length}`,
    )

    // End condition: fewer than BATCH_SIZE remain → reset cycle
    if (remaining.length < BATCH_SIZE) {
      console.log(
        "[poem-batch] markSetCompleted → END CONDITION: less than BATCH_SIZE remain, resetting cycle",
      )
      nextState = initialBatchState()
      const freshBatch = pipe(allIds, shuffle, take(BATCH_SIZE))
      nextState.currentSet = freshBatch
    } else {
      const newSet = pipe(remaining, shuffle, take(BATCH_SIZE))
      nextState = {
        currentSet: newSet,
        previousIds: dedupedPrevious,
        setIsPlayedTimes: 0,
      }
    }
  } else {
    console.log(
      "[poem-batch] markSetCompleted → NO ROTATION",
      `(${nextState.setIsPlayedTimes} < ${numberOfRepeats})`,
    )
  }

  writeState(nextState)
  console.log(
    "[poem-batch] markSetCompleted → AFTER:",
    `setIsPlayedTimes=${nextState.setIsPlayedTimes}`,
    `currentSet.length=${nextState.currentSet.length}`,
    `previousIds.length=${nextState.previousIds.length}`,
  )
  return nextState
}

/** Wipe stored batch state (called when repeats config changes). */
export function resetState(): void {
  console.log("[poem-batch] resetState — clearing batch state")
  localStorage.removeItem(STORAGE_KEY)
}

/**
 * Pick or re-pick the current batch.
 *
 * - If `state.currentSet` is non-empty and `setIsPlayedTimes < numberOfRepeats`,
 *   the current set is reused.
 * - Otherwise a fresh batch of up to `BATCH_SIZE` IDs is drawn (shuffled,
 *   excluding `previousIds`).
 * - The returned `newState` is already persisted.
 */
export function selectNextSet(
  allIds: string[],
  state: PoemBatchState,
  numberOfRepeats: number,
) {
  // Reuse current set if still in progress
  if (
    state.currentSet.length > 0 &&
    state.setIsPlayedTimes < numberOfRepeats
  ) {
    console.log(
      "[poem-batch] selectNextSet → REUSE currentSet",
      `(${state.currentSet.length} ids)`,
      `setIsPlayedTimes=${state.setIsPlayedTimes}`,
      `< numberOfRepeats=${numberOfRepeats}`,
    )
    return { batch: state.currentSet, newState: state }
  }

  console.log(
    "[poem-batch] selectNextSet → PICK NEW.",
    `currentSet.length=${state.currentSet.length}`,
    `setIsPlayedTimes=${state.setIsPlayedTimes}`,
    `numberOfRepeats=${numberOfRepeats}`,
  )

  // Move current set into previousIds
  const dedupedPrevious = [
    ...new Set([...state.previousIds, ...state.currentSet]),
  ]

  // Remaining IDs (never seen as a complete batch)
  const remaining = allIds.filter(id=> !dedupedPrevious.includes(id))

  console.log(
    "[poem-batch] selectNextSet →",
    `remaining=${remaining.length}`,
    `dedupedPrevious=${dedupedPrevious.length}`,
  )

  const newSet = pipe(remaining, shuffle, take(BATCH_SIZE))

  const newState: PoemBatchState = {
    currentSet: newSet,
    previousIds: dedupedPrevious,
    setIsPlayedTimes: 0,
  }

  writeState(newState)
  return { batch: newSet, newState }
}

export function writeState(state: PoemBatchState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

function initialBatchState(): PoemBatchState {
  return { currentSet: [], previousIds: [], setIsPlayedTimes: 0 }
}

function readState(): null | PoemBatchState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    // SAFETY: parsed from JSON, shape is PoemBatchState
    return JSON.parse(raw) as PoemBatchState
  } catch {
    return null
  }
}
